import React, { useEffect, useRef, useState } from "react";
import moment from "moment";
import { LuCalendarDays, LuChevronDown } from "react-icons/lu";

// Date pickers matching the Reporting Dashboard's filter standard: a pill
// button (icon + label + chevron, filled blue once a value is set) that opens
// a popover with labelled date fields and RESET / DONE actions.

const TRIGGER_BASE =
  "tw-inline-flex tw-h-10 tw-items-center tw-justify-between tw-gap-2 tw-rounded-xl tw-border-2 tw-border-solid tw-px-3 tw-text-[12px] tw-font-bold tw-shadow-soft tw-transition-all focus:tw-outline-none focus-visible:tw-ring-1 focus-visible:tw-ring-primary";
const TRIGGER_EMPTY =
  "tw-border-slate-100 tw-bg-white tw-text-slate-700 hover:tw-bg-slate-50";
const TRIGGER_FILLED =
  "tw-border-primary tw-bg-primary tw-text-white hover:tw-bg-primary/95";
const FIELD_LABEL =
  "tw-m-0 tw-mb-1.5 tw-block tw-text-[10px] tw-font-black tw-uppercase tw-tracking-wider tw-text-slate-400";
const FIELD =
  "tw-block tw-h-9 tw-w-full tw-rounded-xl tw-border tw-border-solid tw-border-slate-100 tw-bg-white tw-px-3 tw-text-[12px] tw-font-bold tw-text-foreground focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary/20";

const short = (d) => (d ? moment(d).format("MMM D") : "");
const withYear = (d) => (d ? moment(d).format("MMM D, YYYY") : "");

// Close on outside click / Escape
const usePopover = () => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  return { open, setOpen, ref };
};

const Trigger = ({ filled, icon: Icon, label, title, onClick, open, className = "" }) => (
  <button
    type="button"
    onClick={onClick}
    aria-haspopup="dialog"
    aria-expanded={open}
    title={title}
    className={`${TRIGGER_BASE} ${filled ? TRIGGER_FILLED : TRIGGER_EMPTY} ${className}`}
  >
    <span className="tw-flex tw-min-w-0 tw-items-center tw-gap-2">
      <Icon
        className={`tw-h-4 tw-w-4 tw-shrink-0 ${filled ? "tw-text-white" : "tw-text-primary"}`}
        aria-hidden="true"
      />
      <span className="tw-truncate">{label}</span>
    </span>
    <LuChevronDown
      className={`tw-ml-1 tw-h-3.5 tw-w-3.5 tw-shrink-0 tw-transition-transform ${open ? "tw-rotate-180" : ""} ${
        filled ? "tw-text-white/70" : "tw-text-slate-400"
      }`}
      aria-hidden="true"
    />
  </button>
);

const Panel = ({ align, children, onReset, onDone, presets, onPreset }) => (
  <div
    role="dialog"
    className={`tw-absolute tw-top-full tw-z-50 tw-mt-2 tw-w-[280px] tw-space-y-4 tw-rounded-2xl tw-border tw-border-solid tw-border-slate-100 tw-bg-white tw-p-4 tw-font-sans tw-shadow-2xl ${
      align === "end" ? "tw-right-0" : "tw-left-0"
    }`}
  >
    {presets && presets.length > 0 && (
      <div className="tw-flex tw-flex-wrap tw-gap-1.5">
        {presets.map((p) => (
          <button
            key={p.label}
            type="button"
            onClick={() => onPreset(p)}
            className="tw-h-7 tw-rounded-lg tw-border tw-border-solid tw-border-slate-100 tw-bg-slate-50 tw-px-2.5 tw-text-[11px] tw-font-bold tw-text-slate-600 hover:tw-border-primary/30 hover:tw-text-primary"
          >
            {p.label}
          </button>
        ))}
      </div>
    )}
    {children}
    <div className="tw-flex tw-items-center tw-justify-between tw-border-0 tw-border-t tw-border-solid tw-border-slate-50 tw-pt-2">
      {onReset ? (
        <button
          type="button"
          onClick={onReset}
          className="tw-h-7 tw-rounded-md tw-border-0 tw-bg-transparent tw-px-2 tw-text-[10px] tw-font-black tw-tracking-wider tw-text-rose-500 hover:tw-bg-rose-50 hover:tw-text-rose-600"
        >
          RESET
        </button>
      ) : (
        <span />
      )}
      <button
        type="button"
        onClick={onDone}
        className="tw-h-7 tw-rounded-lg tw-border-0 tw-bg-primary tw-px-4 tw-text-[10px] tw-font-black tw-tracking-wider tw-text-white hover:tw-bg-primary/90"
      >
        DONE
      </button>
    </div>
  </div>
);

/**
 * Range: value = { start, end } as YYYY-MM-DD.
 * `onReset` (optional) restores a default; presets = [{ label, range }].
 */
export const DateRangeFilter = ({
  value,
  onChange,
  onReset,
  presets,
  label = "Date range",
  icon = LuCalendarDays,
  align = "start",
  min,
  max,
  fullWidth = false,
  className = "",
}) => {
  const { open, setOpen, ref } = usePopover();
  const start = value?.start || "";
  const end = value?.end || "";
  const filled = !!(start || end);
  const display = !filled
    ? label
    : start && end
      ? `${short(start)} - ${short(end)}`
      : start
        ? `From ${short(start)}`
        : `Until ${short(end)}`;

  return (
    <div ref={ref} className={`tw-relative ${fullWidth ? "tw-block" : "tw-inline-block"}`}>
      <Trigger
        filled={filled}
        icon={icon}
        label={display}
        title={start && end ? `${withYear(start)} – ${withYear(end)}` : undefined}
        open={open}
        onClick={() => setOpen((v) => !v)}
        className={`tw-min-w-[170px] ${fullWidth ? "tw-w-full" : ""} ${className}`}
      />
      {open && (
        <Panel
          align={align}
          presets={presets}
          onPreset={(p) => {
            onChange(p.range);
            setOpen(false);
          }}
          onReset={onReset}
          onDone={() => setOpen(false)}
        >
          <div>
            <label className={FIELD_LABEL} htmlFor="dateFilterStart">Start Date</label>
            <input
              id="dateFilterStart"
              type="date"
              className={FIELD}
              value={start}
              min={min}
              max={end || max || undefined}
              onChange={(e) => e.target.value && onChange({ start: e.target.value, end })}
            />
          </div>
          <div>
            <label className={FIELD_LABEL} htmlFor="dateFilterEnd">End Date</label>
            <input
              id="dateFilterEnd"
              type="date"
              className={FIELD}
              value={end}
              min={start || min || undefined}
              max={max}
              onChange={(e) => e.target.value && onChange({ start, end: e.target.value })}
            />
          </div>
        </Panel>
      )}
    </div>
  );
};

/** Single date: value = YYYY-MM-DD (or ""). */
export const DateFilter = ({
  value,
  onChange,
  onReset,
  presets,
  label = "Select date",
  fieldLabel = "Date",
  icon = LuCalendarDays,
  align = "start",
  min,
  max,
  format = "ddd, MMM D",
  fullWidth = false,
  className = "",
}) => {
  const { open, setOpen, ref } = usePopover();
  const filled = !!value;

  return (
    <div ref={ref} className={`tw-relative ${fullWidth ? "tw-block" : "tw-inline-block"}`}>
      <Trigger
        filled={filled}
        icon={icon}
        label={filled ? moment(value).format(format) : label}
        title={filled ? withYear(value) : undefined}
        open={open}
        onClick={() => setOpen((v) => !v)}
        className={`tw-min-w-[170px] ${fullWidth ? "tw-w-full" : ""} ${className}`}
      />
      {open && (
        <Panel
          align={align}
          presets={presets}
          onPreset={(p) => {
            onChange(p.value);
            setOpen(false);
          }}
          onReset={onReset}
          onDone={() => setOpen(false)}
        >
          <div>
            <label className={FIELD_LABEL} htmlFor="dateFilterSingle">{fieldLabel}</label>
            <input
              id="dateFilterSingle"
              type="date"
              className={FIELD}
              value={value || ""}
              min={min}
              max={max}
              onChange={(e) => onChange(e.target.value)}
            />
          </div>
        </Panel>
      )}
    </div>
  );
};

// Square icon buttons that sit next to a date filter (prev / next)
export const FilterIconButton = ({ children, className = "", ...rest }) => (
  <button
    type="button"
    className={`tw-inline-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-rounded-xl tw-border-2 tw-border-solid tw-border-slate-100 tw-bg-white tw-text-slate-700 tw-shadow-soft tw-transition-all hover:tw-bg-slate-50 focus:tw-outline-none focus-visible:tw-ring-1 focus-visible:tw-ring-primary disabled:tw-pointer-events-none disabled:tw-opacity-50 ${className}`}
    {...rest}
  >
    {children}
  </button>
);
